import React, { useEffect } from "react";
import { useAssetContext } from "../../DataContext/AssetContext";
import ChartComponent from "../ConfigCharts/StackedBarLine";
import WindmillLoading from "../../Loading/Loading"; 
import ApiErrorMessage from "./ApiErrorMessage"; 

const RevenueChartSetup = () => {
  const {
    revenueAsset,
    revenueMetrics,
    selectedResource,
    setSelectedResource,
    isLoading,
    setIsLoading, 
    selectedFrequency,
    selectedDateRange,
    selectedRevenue,
    useIntervalAggregation,
    resourceNames,
    apiError,
    GroupByrevenueMetrics,
    setRevenueAsset,
    selectedAssetId

    
    
  } = useAssetContext();

  // const [useIntervalAggregation, setUseIntervalAggregation] = useState(true);

  interface ChartData {
    time: string;
    [key: string]: number | string; 
  }

  useEffect(() => {
    setRevenueAsset(null);
  }, [selectedAssetId]);
  
  interface Resource {
    RESOURCE_ID: string;
    RESOURCE_NAME: string;
    [metric: string]: any;
  }

  let activeMetrics: string[] = [];

if (selectedRevenue === "market_type") {
  activeMetrics = GroupByrevenueMetrics;
} else if (selectedRevenue === "product") {
  activeMetrics = GroupByrevenueMetrics;
} else if (selectedRevenue === "award_type") {
  activeMetrics = GroupByrevenueMetrics;
} else if (selectedRevenue === "resource") {
  activeMetrics = resourceNames.filter(name => name !== "ALL");

  // if (selectedResource === "ALL") {
  //   activeMetrics = ["asset_revenue"]; 
  // }
}else if (selectedRevenue === "settlement_charge_name") {
  activeMetrics = GroupByrevenueMetrics;
}
else if (selectedRevenue === "total") {
  activeMetrics = GroupByrevenueMetrics;
} else {
  activeMetrics = Array.isArray(revenueMetrics) ? revenueMetrics : ["DAM", "FMM", "RTM"];
}

 
 const dataSource = [
  ...(Array.isArray(revenueAsset?.RESOURCES) ? revenueAsset.RESOURCES : []),
  revenueAsset, 
];

const cleanDataSource = (dataSource ?? []).filter(Boolean);

const allData =
  selectedResource === "ALL"
    ? [revenueAsset] 
    : cleanDataSource.filter(
        (resource: Resource) => resource.RESOURCE_NAME === selectedResource
      );



    const rawChartData = allData
    .flatMap((resource: Resource | null) => 
      resource && typeof resource === "object"
        ? Object.entries(resource)
            .filter(([key, values]) =>
              key !== "RESOURCE_ID" &&
              key !== "RESOURCE_NAME" &&
              typeof values === "object" &&
              values !== null
            )
            .flatMap(([metric, values]) =>
              values
                ? Object.entries(values).map(([time, value]) => ({
                    time: isNaN(new Date(time).getTime()) ? null : time, 
                    
                    RESOURCE_NAME: resource.RESOURCE_NAME || "Unknown",
                    [metric]:
                      value !== null && value !== undefined ? Number(value) : 0,
                  }))
                : []
            )
        : []
    )
    .filter(entry => entry.time !== null); 

    // console.log("All rawData",revenueAsset)
    const groupedData: { [key: string]: ChartData } = {};

    rawChartData.forEach((entry) => {
      if (!entry || typeof entry !== "object" || !("time" in entry)) return;
      if (typeof entry.time === "string" && entry.time.length === 1 && !isNaN(Number(entry.time))) return;
      if (!entry.time || entry.time === "0") return;
    
      const hourKey = new Date(entry.time).getTime();
      if (isNaN(hourKey)) return;
    
      if (!groupedData[hourKey]) {
        groupedData[hourKey] = { time: entry.time, CUMULATIVE_REVENUE: 0 };
      }
    
      activeMetrics.forEach((metric: string) => {
        if (!groupedData[hourKey][metric]) {
          groupedData[hourKey][metric] = 0;
        }
        const numericValue = Number(entry[metric]);
        groupedData[hourKey][metric] =
          (groupedData[hourKey][metric] as number) +
          (Number.isNaN(numericValue) ? 0 : numericValue);
      });
    
      if (entry.CUMULATIVE_REVENUE !== undefined) {
        const cumulativeValue = Number(entry.CUMULATIVE_REVENUE);
        groupedData[hourKey].CUMULATIVE_REVENUE =
          (groupedData[hourKey].CUMULATIVE_REVENUE as number) +
          (Number.isNaN(cumulativeValue) ? 0 : cumulativeValue);
      }
    });
    
  let aggregatedData = Object.values(groupedData);

  const groupDataByInterval = (interval: number) => {
    const groupedData: { [key: string]: ChartData } = {};
  
    aggregatedData.forEach((entry: ChartData) => {
      const date = new Date(entry.time);
      let key: string = "unknown";
  
      const firstDayOfMTD = new Date();
      firstDayOfMTD.setDate(1);
      firstDayOfMTD.setHours(0, 0, 0, 0);
  
      if (interval < 1440) {
        const hour = date.getHours();
        const roundedMinutes = Math.floor(date.getMinutes() / interval) * interval;
        const datePart = entry.time.includes("T")
          ? entry.time.split("T")[0]
          : new Date(entry.time).toISOString().split("T")[0];
        key = `${datePart}-${hour}-${roundedMinutes}`;
      } else if (interval === 1440) {
        date.setHours(0, 0, 0, 0);
        if (selectedDateRange === "mtd" && date < firstDayOfMTD) {
          return;
        }
        key = date.toISOString().split("T")[0];
      } else if (interval === 10080) {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        weekStart.setHours(0, 0, 0, 0);
        if (selectedDateRange === "mtd" && weekStart < firstDayOfMTD) {
          return;
        }
        key = weekStart.toISOString().split("T")[0];
      } else if (interval === 43200) {
        date.setDate(1);
        date.setHours(0, 0, 0, 0);
        if (selectedDateRange === "mtd" && date < firstDayOfMTD) {
          return;
        }
        key = `${date.getFullYear()}-${date.getMonth() + 1}`;
      }
  
      if (!groupedData[key]) {
        groupedData[key] = { time: entry.time, CUMULATIVE_REVENUE: 0 };
      }
  
      activeMetrics.forEach((metric: string) => {
        if (!groupedData[key][metric]) {
          groupedData[key][metric] = 0;
        }
        const numericValue = Number(entry[metric]);
        groupedData[key][metric] =
          (groupedData[key][metric] as number) +
          (Number.isNaN(numericValue) ? 0 : numericValue);
      });
   
      if (entry.CUMULATIVE_REVENUE !== undefined) {
        const cumulativeValue = Number(entry.CUMULATIVE_REVENUE);
        groupedData[key].CUMULATIVE_REVENUE =
          (groupedData[key].CUMULATIVE_REVENUE as number) +
          (Number.isNaN(cumulativeValue) ? 0 : cumulativeValue);
      }
    });
  
    return Object.values(groupedData);
  };
  
  if (useIntervalAggregation) {
    const minuteIntervals: { [key: string]: number } = {
        hour: 60,
        "15minute": 15,
        "5minute": 5,
    };

    const longIntervals: { [key: string]: number } = {
        day: 1440,  
        week: 10080, 
        month: 43200, 
    };

    const isCustomDateRange = /^\d{4}-\d{2}-\d{2}\s*-\s*\d{4}-\d{2}-\d{2}$/.test(selectedDateRange);

    if (
      ["hour", "15minute", "5minute"].includes(selectedFrequency) && 
      (
        ["minus_7d", "yesterday", "mtd", "ytd","minus_30d"].includes(selectedDateRange) || isCustomDateRange
      )
    ) {
      aggregatedData = groupDataByInterval(minuteIntervals[selectedFrequency]);
    } 
    else if (
      ["day", "week", "month"].includes(selectedFrequency) && 
      (
        ["ytd"].includes(selectedDateRange) 
      )
    ) {
      aggregatedData = groupDataByInterval(longIntervals[selectedFrequency]);
    }
      
}

  
const finalChartData = aggregatedData
.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
.map(entry => ({
  ...entry,
  CUMULATIVE_REVENUE: entry.CUMULATIVE_REVENUE || 0
}));
  // console.log("All data",revenueAsset)
const yAxisLabel = { value: "Dollars", dy: 30 ,offset:0};


// console.log("All data",selectedDateRange)


  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
          <WindmillLoading />
        </div>
      )}
      
      <ChartComponent
        chartData={finalChartData} 
        metrics={activeMetrics}
        xAxis="time"
        legend={activeMetrics}
        yAxisLabel={yAxisLabel} 
        xAxisLabel={["5minute", "15minute", "hour"].includes(selectedFrequency) ? "Time" : "Date"}
        selectedFrequency={selectedFrequency}  
        selectedDateRange={selectedDateRange}
        useIntervalAggregation={useIntervalAggregation}
        lineMetric="CUMULATIVE_REVENUE"  
        lineMetricName="Cumulative Revenue"
        lineColor="#A7E9AF"
        rightYAxisLabel="Cumulative Revenue"
      />
       {/* {!isLoading && finalChartData.length === 0 && !apiError && (
  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 pointer-events-none">
    <p className="loading-text text-center text-gray-500 text-lg">
      No data available for the selected asset
    </p>
  </div>
)} */}
<ApiErrorMessage ApiError={apiError} />


    </div>
  );
};

export default RevenueChartSetup;